import { Dialog, Transition } from '@headlessui/react'
import React, { Fragment, useState } from "react";
import { useTranslation } from 'react-i18next';
import ParticleLoader from "../ParticleLoader";

function CategoryAddConfirmModal({ 
    isOpen, 
    onClose, 
    categoryName, 
    subcodes, 
    onAddParentOnly, 
    onAddWithChildren 
}) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);

    const handleAddParentOnly = async () => {
        setLoading(true);
        try {
            await onAddParentOnly();
            onClose();
        } catch (error) {
            console.error('Error adding parent category:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddWithChildren = async () => {
        setLoading(true);
        try {
            await onAddWithChildren();
            onClose();
        } catch (error) {
            console.error('Error adding category with children:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-10" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-25" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                <Dialog.Title
                                    as="h3"
                                    className="text-lg font-medium leading-6 text-gray-900 mb-4"
                                >
                                    {t('AiCodeDiscovery.AddCategoryOptions', 'Add Category Options')}
                                </Dialog.Title>

                                <div className="mb-4">
                                    <p className="text-sm text-gray-600 mb-2">
                                        {t('AiCodeDiscovery.AddCategoryQuestion', 'How would you like to add the category:')} 
                                        <span className="font-semibold text-gray-800"> "{categoryName}"</span>?
                                    </p>
                                    
                                    {subcodes && subcodes.length > 0 && (
                                        <div className="bg-gray-50 p-3 rounded-lg mt-3">
                                            <p className="text-xs text-gray-600 mb-2">
                                                {t('AiCodeDiscovery.SubcodesPreview', 'This category contains')} {subcodes.length} {t('AiCodeDiscovery.Subcodes', 'subcodes')}:
                                            </p>
                                            <div className="flex flex-wrap gap-1">
                                                {subcodes.slice(0, 3).map((subcode, index) => (
                                                    <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
                                                        {subcode}
                                                    </span>
                                                ))}
                                                {subcodes.length > 3 && (
                                                    <span className="text-xs text-gray-500">
                                                        +{subcodes.length - 3} {t('AiCodeDiscovery.More', 'more')}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="flex flex-col gap-3">
                                    {/* Option 1: Add parent only */}
                                    <button
                                        type="button"
                                        className="w-full p-4 text-left border-2 border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                        onClick={handleAddParentOnly}
                                        disabled={loading}
                                    >
                                        <div>
                                            <div className="font-medium text-gray-900">
                                                {t('AiCodeDiscovery.AddParentOnly', 'Add parent category only')}
                                            </div>
                                            <div className="text-sm text-gray-600">
                                                {t('AiCodeDiscovery.AddParentOnlyDesc', 'Create only the main category without subcodes')}
                                            </div>
                                        </div>
                                    </button>

                                    {/* Option 2: Add parent with children */}
                                    {subcodes && subcodes.length > 0 && (
                                        <button
                                            type="button"
                                            className="w-full p-4 text-left border-2 border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                            onClick={handleAddWithChildren}
                                            disabled={loading}
                                        >
                                            <div>
                                                <div className="font-medium text-gray-900">
                                                    {t('AiCodeDiscovery.AddWithChildren', 'Add category with all subcodes')}
                                                </div>
                                                <div className="text-sm text-gray-600">
                                                    {t('AiCodeDiscovery.AddWithChildrenDesc', `Create the main category and all ${subcodes.length} subcodes with different colors`)}
                                                </div>
                                            </div>
                                        </button>
                                    )}
                                </div>

                                <div className="mt-6 flex justify-end">
                                    <button
                                        type="button"
                                        className="inline-flex justify-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 disabled:opacity-50"
                                        onClick={onClose}
                                        disabled={loading}
                                    >
                                        {t('Common.Cancel', 'Cancel')}
                                    </button>
                                </div>

                                {loading && (
                                    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-2xl">
                                        <div className="flex items-center gap-2">
                                            <ParticleLoader size={20} />
                                            <span className="text-sm text-gray-600">
                                                {t('AiCodeDiscovery.CreatingCodes', 'Creating codes...')}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}

export default CategoryAddConfirmModal;